// components/faqPage.js
import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ChevronDown, ChevronUp, HelpCircle, Home, Mail, Shield } from 'lucide-react';

const FAQPage = () => {
  const navigate = useNavigate();
  const [openItems, setOpenItems] = useState(new Set());

  const toggleItem = (index) => {
    const newOpenItems = new Set(openItems);
    if (newOpenItems.has(index)) {
      newOpenItems.delete(index);
    } else {
      newOpenItems.add(index);
    }
    setOpenItems(newOpenItems);
  };

  const faqData = [
    {
      category: "About CPR Refresh",
      questions: [
        {
          question: "What is CPR Refresh?",
          answer: "CPR Refresh is a free educational platform designed to help you maintain your CPR knowledge through monthly quiz reminders. We send you a short 10-question quiz each month to keep life-saving skills fresh in your memory."
        },
        {
          question: "Who can use CPR Refresh?",
          answer: "Anyone interested in maintaining their CPR knowledge! Whether you're a healthcare professional, first aid trained individual, parent, teacher, or someone who wants to be prepared for emergencies, CPR Refresh is designed for you."
        },
        {
          question: "Is CPR Refresh free?",
          answer: "Yes, CPR Refresh is completely free to use. We believe life-saving knowledge should be accessible to everyone."
        },
        {
          question: "Can CPR Refresh replace formal CPR training?",
          answer: "No, CPR Refresh is designed to supplement, not replace, formal CPR training. We strongly recommend completing a hands-on CPR course with a certified instructor. Our quizzes help maintain the knowledge you've already learned."
        }
      ]
    },
    {
      category: "Quiz & Content",
      questions: [
        {
          question: "How many questions are in each quiz?",
          answer: "Each quiz contains 10 randomly selected questions from our database of over 65 CPR-related questions."
        },
        {
          question: "Where do the quiz questions come from?",
          answer: "All questions are based on current Australian Resuscitation Council guidelines and other authoritative Australian sources including St John Ambulance NSW, Victoria, and WA."
        },
        {
          question: "How long does a quiz take?",
          answer: "Most people complete a quiz in 3-5 minutes. Each question provides immediate feedback with explanations, so you can learn as you go."
        },
        {
          question: "Can I retake the quiz?",
          answer: "Absolutely! You can retake the quiz as many times as you want. Each time you'll get a different set of 10 questions from our database."
        },
        {
          question: "Are the guidelines Australian-specific?",
          answer: "Yes, all content follows Australian Resuscitation Council guidelines and uses Australian emergency procedures (like calling 000)."
        }
      ]
    },
    {
      category: "Email Reminders",
      questions: [
        {
          question: "How often will I receive reminder emails?",
          answer: "We send reminder emails once per month to help you maintain your CPR knowledge without being overwhelming."
        },
        {
          question: "Can I change how often I receive emails?",
          answer: "Currently, we offer monthly reminders. If you'd like different frequency options, please contact us at dan@cprrefresh.com with your feedback."
        },
        {
          question: "What if I miss a monthly reminder?",
          answer: "No problem! You can visit the website anytime to take a quiz. The reminders are just to help you remember - you're not limited to once per month."
        },
        {
          question: "How do I unsubscribe from reminder emails?",
          answer: "You can unsubscribe at any time by clicking the unsubscribe link in any email, or by visiting our unsubscribe page on the website."
        },
        {
          question: "Will you sell my email address?",
          answer: "Never. We respect your privacy and will never sell, trade, or share your email address. Read our full privacy policy for more details."
        }
      ]
    },
    {
      category: "Privacy & Security",
      questions: [
        {
          question: "How is my personal information protected?",
          answer: "We use industry-standard security measures including reCAPTCHA v3 to protect against spam and secure data transmission. We only collect your email address for quiz reminders."
        },
        {
          question: "Do you track my quiz performance?",
          answer: "We use Google Analytics to understand how people use our site (like completion rates), but we don't store individual quiz scores linked to your email address."
        },
        {
          question: "Can I delete my data?",
          answer: "Yes, you have the right to request deletion of your personal information. Contact us using our contact form to request data deletion."
        }
      ]
    },
    {
      category: "Technical Support",
      questions: [
        {
          question: "The quiz isn't loading. What should I do?",
          answer: "Try refreshing your browser or clearing your cache. If the problem persists, please contact us using our contact form with details about your browser and device."
        },
        {
          question: "I'm not receiving reminder emails. Why?",
          answer: "Check your spam/junk folder first. If emails aren't there, contact us using our contact form and we'll check your subscription status."
        },
        {
          question: "Does the site work on mobile devices?",
          answer: "Yes! CPR Refresh is fully responsive and works on smartphones, tablets, and desktop computers."
        },
        {
          question: "Which browsers are supported?",
          answer: "CPR Refresh works on all modern browsers including Chrome, Firefox, Safari, and Edge. For the best experience, please keep your browser updated."
        }
      ]
    },
    {
      category: "Feedback & Contact",
      questions: [
        {
          question: "How can I suggest new quiz questions?",
          answer: "We'd love your input! Use our contact form to send your suggested questions (with sources). All questions must be based on current Australian guidelines."
        },
        {
          question: "I found an error in a quiz question. How do I report it?",
          answer: "Please contact us immediately using our contact form with details about the question and the issue. We take accuracy very seriously."
        },
        {
          question: "Can I provide feedback about the site?",
          answer: "Absolutely! We welcome all feedback, suggestions, and ideas. Contact us using our contact form."
        },
        {
          question: "How can I contact CPR Refresh?",
          answer: "Use our contact form for any questions, concerns, or feedback. We aim to respond within 24-48 hours."
        }
      ]
    }
  ];

  return (
    <div className="bg-gradient-to-br from-blue-50 to-indigo-100">
      <main className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div className="text-center mb-12">
          <div className="inline-flex items-center justify-center w-16 h-16 bg-indigo-100 rounded-full mb-4">
            <HelpCircle className="h-8 w-8 text-indigo-600" />
          </div>
          <h1 className="text-4xl font-bold text-gray-900 mb-4">Frequently Asked Questions</h1>
          <p className="text-xl text-gray-600 max-w-2xl mx-auto">
            Find answers to common questions about CPR Refresh, our quizzes, and how we help you maintain life-saving knowledge.
          </p>
        </div>

        <div className="space-y-8">
          {faqData.map((category, categoryIndex) => (
            <div key={categoryIndex} className="bg-white rounded-2xl shadow-lg overflow-hidden">
              <div className="bg-indigo-600 px-6 py-4">
                <h2 className="text-xl font-semibold text-white flex items-center">
                  {category.category === "About CPR Refresh" && <HelpCircle className="mr-3 h-5 w-5" />}
                  {category.category === "Privacy & Security" && <Shield className="mr-3 h-5 w-5" />}
                  {category.category === "Feedback & Contact" && <Mail className="mr-3 h-5 w-5" />}
                  {!["About CPR Refresh", "Privacy & Security", "Feedback & Contact"].includes(category.category) && <HelpCircle className="mr-3 h-5 w-5" />}
                  {category.category}
                </h2>
              </div>
              <div className="divide-y divide-gray-200">
                {category.questions.map((item, itemIndex) => {
                  const globalIndex = `${categoryIndex}-${itemIndex}`;
                  const isOpen = openItems.has(globalIndex);
                  
                  return (
                    <div key={itemIndex}>
                      <button
                        onClick={() => toggleItem(globalIndex)}
                        className="w-full px-6 py-4 text-left hover:bg-gray-50 focus:outline-none focus:bg-gray-50 transition-colors duration-200"
                      >
                        <div className="flex justify-between items-center">
                          <h3 className="text-lg font-medium text-gray-900 pr-4">
                            {item.question}
                          </h3>
                          <div className="flex-shrink-0">
                            {isOpen ? (
                              <ChevronUp className="h-5 w-5 text-indigo-600" />
                            ) : (
                              <ChevronDown className="h-5 w-5 text-gray-400" />
                            )}
                          </div>
                        </div>
                      </button>
                      
                      {isOpen && (
                        <div className="px-6 pb-4">
                          <div className="prose prose-gray max-w-none">
                            <p className="text-gray-600 leading-relaxed">
                              {item.answer}
                            </p>
                          </div>
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
          ))}
        </div>

        {/* Contact CTA */}
        <div className="mt-12 text-center">
          <div className="bg-white rounded-2xl shadow-lg p-8">
            <h3 className="text-2xl font-bold text-gray-900 mb-4">Still have questions?</h3>
            <p className="text-gray-600 mb-6">
              Can't find what you're looking for? We're here to help!
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <button
                onClick={() => navigate('/contact')}
                className="inline-flex items-center px-6 py-3 border border-transparent text-base font-medium rounded-lg text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 transition-colors"
              >
                <Mail className="mr-2 h-4 w-4" />
                Contact Us
              </button>
              <button
                onClick={() => navigate('/')}
                className="inline-flex items-center px-6 py-3 border border-indigo-600 text-base font-medium rounded-lg text-indigo-600 bg-white hover:bg-indigo-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 transition-colors"
              >
                <Home className="mr-2 h-4 w-4" />
                Back to Home
              </button>
            </div>
          </div>
        </div>
      </main>
    </div>
  );
};

export default FAQPage;